import { useState, useRef, FormEvent } from 'react';

export default function ContactUs() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('idle');
    setErrorMsg('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setStatus('success');
      return;
    }

    const formData = new FormData(form);

    try {
      const res = await fetch('https://readdy.ai/api/form/d9fq594bl0ahdbsf9tng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        formRef.current?.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || 'Something went wrong.';
        if (serverMsg.toLowerCase().includes('spam')) {
          setStatus('error');
          setErrorMsg('Your message could not be sent. Please try again.');
        } else {
          setStatus('error');
          setErrorMsg(serverMsg);
        }
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please check your connection and try again.');
    }
  };

  const socialLinks = [
    { platform: 'TikTok', handle: '@dcure.gelato.bake', url: 'https://www.tiktok.com/@dcure.gelato.bake', icon: 'ri-tiktok-fill' },
    { platform: 'Instagram', handle: '@dcuregelato', url: 'https://www.instagram.com/dcuregelatomelaka/?hl=en', icon: 'ri-instagram-line' },
  ];

  return (
    <section id="contact" className="relative w-full py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://storage.readdy-site.link/project_files/67b99c16-3b28-4507-9f93-7295101e045c/62834b84-fddd-4696-8565-e5d952f5d256_compressed_Screenshot-2026-07-22-014720.webp"
          alt="Cozy cafe seating area at D'CURE Gelato"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-background-950/80"></div>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <h2 className="font-heading text-4xl md:text-5xl text-primary-300 glow-text-strong">
            Connect with D'cure!
          </h2>
          <div className="w-12 h-[2px] bg-primary-500/50 mx-auto mt-4"></div>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          <div className="flex-1">
            <div className="flex flex-wrap gap-4 mb-10">
              {socialLinks.map((link) => (
                <a
                  key={link.platform}
                  href={link.url}
                  className="inline-flex items-center gap-2.5 bg-primary-500/15 border border-primary-400/20 text-primary-300 font-label text-sm font-medium px-6 py-3 rounded-full hover:bg-primary-500 hover:text-background-950 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className={`${link.icon} text-lg`}></i>
                  <span>{link.platform}: {link.handle}</span>
                </a>
              ))}
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-500/10 border border-primary-400/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-lg text-primary-400"></i>
                </div>
                <div>
                  <p className="font-label text-xs text-foreground-300/60 tracking-wider uppercase">Phone</p>
                  <p className="font-body text-base text-foreground-100">011-8888-2546</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-500/10 border border-primary-400/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-lg text-primary-400"></i>
                </div>
                <div>
                  <p className="font-label text-xs text-foreground-300/60 tracking-wider uppercase">Email</p>
                  <p className="font-body text-base text-foreground-100">dlatomelaka@gmail.com</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-500/10 border border-primary-400/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-lg text-primary-400"></i>
                </div>
                <div>
                  <p className="font-label text-xs text-foreground-300/60 tracking-wider uppercase">Opening Hours</p>
                  <p className="font-body text-base text-foreground-100">Mon – Sun ( except Tuesday ) : 11:30 AM – 7:30 PM</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-primary-500/10 border border-primary-400/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-lg text-primary-400"></i>
                </div>
                <div>
                  <p className="font-label text-xs text-foreground-300/60 tracking-wider uppercase">Address</p>
                  <p className="font-body text-base text-foreground-100 leading-relaxed">
                    63, Jalan Kampung Pantai,<br />
                    75200 Melaka, Malaysia
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <h3 className="font-heading text-2xl text-primary-400 glow-text mb-5">Send Us a Message</h3>
              <form ref={formRef} onSubmit={handleSubmit} data-readdy-form="" className="space-y-4 max-w-lg">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    required
                    className="w-full bg-background-50/10 border border-foreground-200/20 rounded-lg px-4 py-3 text-sm font-label text-foreground-100 placeholder:text-foreground-400/50 focus:outline-none focus:border-primary-400/50 transition-colors"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    required
                    className="w-full bg-background-50/10 border border-foreground-200/20 rounded-lg px-4 py-3 text-sm font-label text-foreground-100 placeholder:text-foreground-400/50 focus:outline-none focus:border-primary-400/50 transition-colors"
                  />
                </div>
                <textarea
                  name="message"
                  placeholder="Your Message"
                  rows={4}
                  maxLength={500}
                  required
                  className="w-full bg-background-50/10 border border-foreground-200/20 rounded-lg px-4 py-3 text-sm font-label text-foreground-100 placeholder:text-foreground-400/50 focus:outline-none focus:border-primary-400/50 transition-colors resize-none"
                ></textarea>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  className="bg-primary-500 text-background-950 font-label text-sm font-semibold tracking-[0.1em] px-8 py-3 rounded-full hover:bg-primary-400 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  SEND MESSAGE
                </button>
                {status === 'success' && (
                  <p className="font-label text-sm text-green-400 mt-2">Thank you! Your message has been sent. We'll get back to you soon!</p>
                )}
                {status === 'error' && (
                  <p className="font-label text-sm text-red-400 mt-2">{errorMsg}</p>
                )}
              </form>
            </div>
          </div>

          <div className="flex-1">
            <div className="rounded-2xl overflow-hidden h-[450px] lg:h-full min-h-[400px] border border-foreground-200/10">
              <iframe
                src="https://www.google.com/maps?q=DCURE+GELATO+BAKERY+63+Jalan+Kampung+Pantai+75200+Melaka&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="D'CURE Gelato Bakery Location - 63 Jalan Kampung Pantai, 75200 Melaka"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}